import { asText } from '~/utils/searchParams';

/**
 * The `?redirect=` return-to that `useAuthCheck` writes when it bounces an
 * anonymous visitor off an authenticated route, and that the login screen
 * consumes after sign-in.
 *
 * Shared because two places read it: the login form's success handler, and
 * `useAuthCheck`'s bounce of a signed-in user off /login, which fires on the
 * same render and would otherwise overwrite the form's navigation with `/`.
 */

/**
 * A same-origin return-to, or nothing. Only paths starting with a single `/`
 * are accepted, which rejects `//host`, `/\host` and anything with a scheme, so
 * a crafted `?redirect=` can't send a freshly signed-in visitor off this
 * origin.
 *
 * It also keeps navigation client-side: `router.navigate` does a full page load
 * for anything `new URL(href)` parses, which only an absolute URL does.
 */
export function asInternalHref(value: unknown): string | undefined {
  const text = asText(value);
  if (text === undefined) return undefined;
  if (!text.startsWith('/')) return undefined;
  if (text.startsWith('//') || text.startsWith('/\\')) return undefined;
  // A return-to that points back at the sign-in screens is never useful, and
  // accepting one is how a stale nested `?redirect=` becomes a login loop.
  if (/^\/(login|register)(\/|\?|$)/.test(text)) return undefined;
  return text;
}
